import { useMemo } from 'react'
import { useContent } from '../../hooks/useContent.js'
import { useLocale } from '../../i18n/index.js'

/**
 * Terms, privacy policy, cookies. The text lives in the CMS `pages` table
 * (content.body) so Diana — or her lawyer — can edit it without a deploy.
 *
 * The body is plain text with a little structure, deliberately NOT markdown:
 * `## ` starts a heading, `- ` starts a bullet, blank lines separate
 * paragraphs. That's the whole grammar — nothing for pasted legal text to
 * break on.
 */
export function LegalPage({ slug }: { slug: 'regulamin' | 'prywatnosc' | 'cookies' }) {
  const locale = useLocale()
  const { data, isLoading } = useContent(locale)

  const page = data?.pages[slug]
  const body = typeof page?.content?.body === 'string' ? page.content.body : ''
  const blocks = useMemo(() => parseBody(body), [body])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl animate-pulse px-5 pt-24 lg:px-8">
        <div className="h-10 w-2/3 bg-sky-100" />
        <div className="mt-8 h-4 w-full bg-sky-100" />
        <div className="mt-3 h-4 w-5/6 bg-sky-100" />
      </div>
    )
  }

  return (
    <article className="mx-auto max-w-3xl px-5 pb-8 pt-16 lg:px-8 lg:pt-24">
      <h1 className="h-display text-4xl leading-tight sm:text-5xl">{page?.title ?? ''}</h1>
      <div className="rule-gold mt-7 w-24" />

      <div className="mt-10 space-y-5">
        {blocks.map((block, i) =>
          block.kind === 'heading' ? (
            <h2 key={i} className="h-condensed pt-6 text-lg text-ink">
              {block.text}
            </h2>
          ) : block.kind === 'list' ? (
            <ul key={i} className="list-disc space-y-2 pl-6 leading-relaxed font-light text-ink-soft">
              {block.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          ) : (
            <p key={i} className="leading-relaxed font-light text-ink-soft">
              {block.text}
            </p>
          ),
        )}
      </div>
    </article>
  )
}

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] }

function parseBody(body: string): Block[] {
  const blocks: Block[] = []

  for (const raw of body.split(/\n{2,}/)) {
    const chunk = raw.trim()
    if (!chunk) continue

    if (chunk.startsWith('## ')) {
      // A heading may share a chunk with the paragraph typed right under it.
      const [first, ...rest] = chunk.split('\n')
      blocks.push({ kind: 'heading', text: first!.slice(3).trim() })
      const remainder = rest.join('\n').trim()
      if (remainder) blocks.push(...parseBody(remainder))
      continue
    }

    const lines = chunk.split('\n').map((l) => l.trim())
    if (lines.every((l) => l.startsWith('- '))) {
      blocks.push({ kind: 'list', items: lines.map((l) => l.slice(2).trim()) })
      continue
    }

    blocks.push({ kind: 'paragraph', text: lines.join(' ') })
  }

  return blocks
}
